import React, { useState } from 'react';
import { useModal } from '@app/components/modal-views/context';
import Button from '@app/components/ui/button';
import WarningIcon from '@app/components/icons/warning';
import { toast } from 'react-toastify';
import { useDeletePdfMutation, usePermanentlyDeletePdfMutation } from '@app/store/pdf/api';
import { pdfResponseDto } from '@app/store/pdf/types';
import { Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { ToastId } from '@app/constants/ToastId';
import { selectPdfInfo, setSelectedRows, setToggleCleared } from '@app/store/pdf/slice';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';

export default function DeleteConfirmationModal({ documents, fromRecyclebin }: { documents: Array<pdfResponseDto>; fromRecyclebin: boolean }) {
    const { closeModal } = useModal();
    const [deletePdf] = useDeletePdfMutation();
    const [permanentlyDeletePdf] = usePermanentlyDeletePdfMutation();
    const router = useRouter();
    const pdf = useAppSelector(selectPdfInfo);
    const dispatch = useAppDispatch();
    const { t } = useTranslation(['common', 'modal', 'document']);
    const [isLoading, setLoading] = useState(false);
    const handleDelete = async () => {
        setLoading(true);
        await deletePdf({
            organizationId: router.query.organizationId as string,
            projectId: router.query.projectId as string,
            documentIds: documents.map((document) => document.documentId)
        }).then((response) => {
            if ('data' in response) {
                closeModal();
                dispatch(setSelectedRows([]));
                dispatch(setToggleCleared(!pdf.toggleCleared));
                toast(`${t('documentRemoved', { ns: 'document' })}`, {
                    type: 'success',
                    toastId: ToastId.DOCUMENT_SUCCESS_TOAST
                });
            } else {
                toast(`${t('toast.failedToDeletePdf')}`, { type: 'error', toastId: ToastId.DOCUMENT_ERROR_TOAST });
            }
            setLoading(false);
        });
    };

    const handlePermanentDelete = async () => {
        setLoading(true);
        await permanentlyDeletePdf({
            organizationId: router.query.organizationId as string,
            projectId: router.query.projectId as string,
            documentIds: documents.map((document) => document.documentId)
        }).then((response) => {
            if ('data' in response) {
                closeModal();
                dispatch(setSelectedRows([]));
                dispatch(setToggleCleared(!pdf.toggleCleared));
                toast(`${t('documentRemovedPermanently', { ns: 'document' })}`, {
                    type: 'success',
                    toastId: ToastId.DOCUMENT_SUCCESS_TOAST
                });
            } else {
                toast(`${t('toast.failedToDeletePdf')}`, { type: 'error', toastId: ToastId.DOCUMENT_ERROR_TOAST });
            }
            setLoading(false);
        });
    };
    return (
        <div data-testid="delete-modal" className="flex w-full max-w-[500px] flex-col gap-5 rounded-xl border-[1px] border-gray-300 bg-white ">
            <div className="mx-1 flex flex-col items-center gap-5 border-b-[1px] border-gray-200 px-6 py-4">
                <WarningIcon />
                <div className={'h4 text-center font-medium text-gray-900'}>
                    {t('areYouSureYouWantToDelete', { ns: 'modal' })}
                    <Typography className="label break-all"> {documents.length === 1 ? `”${documents[0].documentName}”` : `${documents.length} ${t('documents', { ns: 'document' })}`}?</Typography>
                    {fromRecyclebin && <Typography className="py-3 text-gray-500">{t('permanentlyDeleteDescription', { ns: 'modal' })}</Typography>}
                </div>
            </div>
            <div className="grid w-full !max-w-[500px] grid-rows-2 gap-2 px-4 pb-4 sm:grid-cols-2 sm:grid-rows-1 sm:gap-6">
                <Button className={'h-[45px]  w-full !border-gray-300 !bg-white  !text-gray-900 hover:!bg-gray-50 hover:!text-gray-900'} variant="outline" onClick={closeModal}>
                    {t('cancel', { ns: 'common' })}
                </Button>
                <Button isLoading={isLoading} className={'h-[45px] w-full rounded-[4px] !bg-red-500 hover:!bg-red-600'} variant={'solid'} onClick={fromRecyclebin ? handlePermanentDelete : handleDelete}>
                    {t('deleteNow')}
                </Button>
            </div>
        </div>
    );
}
